/**
 * Storefront v6 — the shell: announcement bar, header (floats over the cover
 * photo, then paper), the phone menu sheet, and the footer on the derived
 * dark surface.
 */
import React, { useEffect, useState } from 'react';
import { ArrowRight, Menu as MenuIcon, Search, ShoppingBag, User, X, MapPin, Phone, Mail, Instagram, Facebook, Globe } from 'lucide-react';

export const AnnouncementBar = ({ items = [] }) => {
  const list = (Array.isArray(items) ? items : []).map((s) => String(s || '').trim()).filter(Boolean);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [interacting, setInteracting] = useState(false);
  const signature = JSON.stringify(list);
  useEffect(() => { setIndex(0); }, [signature]);
  useEffect(() => {
    if (list.length < 2 || paused || interacting) return undefined;
    const timer = setInterval(() => setIndex(current => (current + 1) % list.length), 6000);
    return () => clearInterval(timer);
  }, [list.length, paused, interacting]);
  if (!list.length) return null;
  return (
    <div className="s6-announce s6-announce-rotating" data-testid="s6-announce" role="region" aria-label="Store announcements"
      onMouseEnter={() => setInteracting(true)} onMouseLeave={() => setInteracting(false)}
      onFocus={() => setInteracting(true)} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setInteracting(false); }}>
      <span className="s6-announce-message" aria-live={paused || interacting ? 'polite' : 'off'}>{list[index % list.length]}</span>
      {list.length > 1 && <div className="s6-announce-controls">
        <button type="button" aria-label="Previous announcement" onClick={() => setIndex((index + list.length - 1) % list.length)}>‹</button>
        <span className="text-xs tabular-nums">{index % list.length + 1}/{list.length}</span>
        <button type="button" aria-label="Next announcement" onClick={() => setIndex((index + 1) % list.length)}>›</button>
        <button type="button" aria-label={paused ? 'Resume announcements' : 'Pause announcements'} onClick={() => setPaused(!paused)}>{paused ? '▶' : 'Ⅱ'}</button>
      </div>}
    </div>
  );
};

/**
 * Header. `overlay` = floating over the hero photo (white text, no ground);
 * `items` = [{ key, label, onClick, testid?, primary?, hidden? }].
 */
export const Header = ({
  brand, theme, items = [], overlay = false, cartCount = 0, onCart, onSearch, onAccount, accountLabel = 'Sign in',
  headerRef, onMenu, menuOpen = false, onHome,
}) => (
  <header
    ref={headerRef}
    className={`s6-header ${overlay ? 's6-header-overlay s6-on-photo' : ''}`}
    data-testid="s6-header"
    data-overlay={overlay ? 'true' : 'false'}
  >
    <div className="s6-container h-16 md:h-[72px] flex items-center justify-between gap-3">
      <button type="button" onClick={onHome} className="flex items-center gap-2.5 min-w-0" aria-label={brand.brand_name || 'Home'} data-testid="s6-brand">
        {brand.logo_url ? (
          <img src={brand.logo_url} alt="" className="h-9 w-9 md:h-10 md:w-10 object-contain flex-shrink-0" />
        ) : (
          <span className="h-9 w-9 md:h-10 md:w-10 rounded-full grid place-items-center flex-shrink-0 font-bold" style={{ background: 'var(--s6-primary)', color: 'var(--s6-on-primary)', fontFamily: 'var(--s6-heading)' }}>
            {(brand.brand_name || 'M').charAt(0)}
          </span>
        )}
        <span className="truncate max-w-[40vw] sm:max-w-[44vw] lg:max-w-none text-[16px] md:text-lg font-bold tracking-tight" style={{ fontFamily: 'var(--s6-heading)' }}>
          {brand.brand_name}
        </span>
      </button>

      <nav className="flex items-center gap-1 flex-shrink-0" aria-label="Storefront" data-testid="s6-nav">
        {items.filter((it) => !it.primary).map((it) => (
          <button key={it.key} type="button" onClick={it.onClick} className="hidden lg:inline-flex s6-nav-link" data-testid={it.testid || `s6-nav-${it.key}`} aria-current={it.active ? 'true' : undefined}>
            {it.label}
          </button>
        ))}
        {/* Wrapped: .s6-icon-btn sets display, which would beat Tailwind's `hidden` on the button itself */}
        {onSearch && (
          <span className="hidden md:inline-flex">
            <button type="button" onClick={onSearch} className="s6-icon-btn" aria-label="Search the menu" data-testid="s6-search">
              <Search className="w-5 h-5" />
            </button>
          </span>
        )}
        {onAccount && (
          <span className="hidden md:inline-flex">
            <button type="button" onClick={onAccount} className="s6-icon-btn" aria-label={accountLabel} data-testid="s6-account">
              <User className="w-5 h-5" />
            </button>
          </span>
        )}
        <button type="button" onClick={onCart} className="s6-icon-btn relative !w-10 !h-10 md:!w-11 md:!h-11" aria-label={`Cart, ${cartCount} items`} data-testid="s6-cart">
          <ShoppingBag className="w-5 h-5" />
          {cartCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-bold grid place-items-center" style={{ background: 'var(--s6-primary)', color: 'var(--s6-on-primary)' }} data-testid="s6-cart-count">
              {cartCount}
            </span>
          )}
        </button>
        {items.filter((it) => it.primary).map((it) => (
          <button key={it.key} type="button" onClick={it.onClick} className="s6-btn s6-btn-order ml-0.5 md:ml-1 !min-h-[38px] md:!min-h-[40px] !px-3 md:!px-4 text-[13px] md:text-sm max-w-[126px] sm:max-w-[220px]" aria-label={it.label} title={it.label} data-testid={it.testid || `s6-nav-${it.key}`}>
            <span className="truncate sm:hidden">{it.mobileLabel || it.shortLabel || it.label}</span><span className="hidden sm:block truncate">{it.shortLabel || it.label}</span>
          </button>
        ))}
        <span className="lg:hidden">
          <button type="button" onClick={onMenu} className="s6-icon-btn !w-10 !h-10" aria-label="Menu" aria-expanded={menuOpen ? 'true' : 'false'} data-testid="s6-menu-open">
            <MenuIcon className="w-5 h-5" />
          </button>
        </span>
      </nav>
    </div>
  </header>
);

/** Full-screen phone menu; locks the page and closes on Escape. */
export const MobileMenuSheet = ({ open, onClose, brand, items = [] }) => {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="s6-menu-sheet lg:hidden" role="dialog" aria-modal="true" aria-label="Menu" data-testid="s6-menu-sheet">
      <div className="flex items-center justify-between px-5 h-16 border-b" style={{ borderColor: 'var(--s6-line)' }}>
        <span className="text-lg font-bold truncate" style={{ fontFamily: 'var(--s6-heading)' }}>{brand.brand_name}</span>
        <button type="button" onClick={onClose} className="s6-icon-btn" aria-label="Close menu" data-testid="s6-menu-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 pt-1">
        {items.filter((it) => !it.primary).map((it) => (
          <button key={it.key} type="button" onClick={() => { onClose(); it.onClick(); }} className="s6-menu-link" data-testid={`s6-menu-${it.key}`}>
            {it.label}<ArrowRight className="w-5 h-5 opacity-50" aria-hidden="true" />
          </button>
        ))}
      </div>
      <div className="px-5 pb-8 pt-3">
        {items.filter((it) => it.primary).map((it) => (
          <button key={it.key} type="button" onClick={() => { onClose(); it.onClick(); }} className="s6-btn s6-btn-order w-full !min-h-[52px]">
            {it.label}<ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
        ))}
      </div>
    </div>
  );
};

const hoursRows = (operatingHours) => {
  if (!operatingHours || typeof operatingHours !== 'object') return [];
  const order = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const label = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };
  return order.filter((d) => operatingHours[d]).map((d) => {
    const h = operatingHours[d] || {};
    const closed = h.closed === true || h.is_closed === true || (!h.open && !h.close);
    return { key: d, label: label[d], text: closed ? 'Closed' : `${h.open || ''} – ${h.close || ''}` };
  });
};
export { hoursRows };

export const Footer = ({ brand, settings, items = [], poweredBy = false, onNav }) => {
  const rows = settings?.store_hours_enabled ? hoursRows(settings?.operating_hours) : [];
  const hasContact = !!(brand.contact_address || brand.contact_phone || brand.contact_email);
  const socials = [
    brand.instagram_url && { href: brand.instagram_url, Icon: Instagram, label: 'Instagram' },
    brand.facebook_url && { href: brand.facebook_url, Icon: Facebook, label: 'Facebook' },
    brand.website_url && { href: brand.website_url, Icon: Globe, label: 'Website' },
  ].filter(Boolean);
  return (
    <footer id="footer" className="s6-surface-dark s6-on-dark" data-testid="s6-footer">
      <div className="s6-container py-12 md:py-16 grid gap-10 md:grid-cols-12">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3">
            {brand.logo_url && <img src={brand.logo_url} alt="" className="h-10 w-10 object-contain" />}
            <span className="text-xl font-bold" style={{ fontFamily: 'var(--s6-heading)' }}>{brand.brand_name}</span>
          </div>
          {brand.tagline && <p className="mt-3 text-sm opacity-75 max-w-xs">{brand.tagline}</p>}
          {socials.length > 0 && (
            <div className="mt-5 flex gap-2">
              {socials.map(({ href, Icon, label }) => (
                <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label} className="w-10 h-10 rounded-full grid place-items-center border" style={{ borderColor: 'rgba(255,255,255,0.25)' }}>
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          )}
        </div>
        {hasContact && (
          <div className={rows.length > 0 ? 'md:col-span-3' : 'md:col-span-4'}>
            <h3 className="s6-h3 mb-3">Contact</h3>
            <ul className="space-y-2 text-sm opacity-85">
              {brand.contact_address && <li className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />{brand.contact_address}</li>}
              {brand.contact_phone && <li className="flex gap-2"><Phone className="w-4 h-4 mt-0.5 flex-shrink-0" /><a href={`tel:${brand.contact_phone}`}>{brand.contact_phone}</a></li>}
              {brand.contact_email && <li className="flex gap-2"><Mail className="w-4 h-4 mt-0.5 flex-shrink-0" /><a href={`mailto:${brand.contact_email}`}>{brand.contact_email}</a></li>}
            </ul>
          </div>
        )}
        {rows.length > 0 && (
          <div className={hasContact ? 'md:col-span-3' : 'md:col-span-4'}>
            <h3 className="s6-h3 mb-3">Hours</h3>
            <ul className="space-y-1 text-sm opacity-85 tabular-nums">
              {rows.map((r) => <li key={r.key} className="flex justify-between gap-4"><span>{r.label}</span><span>{r.text}</span></li>)}
            </ul>
          </div>
        )}
        <div className={hasContact && rows.length > 0 ? 'md:col-span-2' : 'md:col-span-4'}>
          <h3 className="s6-h3 mb-3">Links</h3>
          <ul className="space-y-2 text-sm opacity-85">
            {items.filter((it) => !it.primary).map((it) => (
              <li key={it.key}><button type="button" onClick={it.onClick} className="hover:underline underline-offset-4">{it.label}</button></li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t" style={{ borderColor: 'rgba(255,255,255,0.12)' }}>
        <div className="s6-container py-5 flex flex-wrap items-center justify-between gap-3 text-xs opacity-70">
          <span>© {new Date().getFullYear()} {brand.brand_name}</span>
          {poweredBy && (
            <a href="https://plateos.io" target="_blank" rel="noreferrer" className="hover:underline" data-testid="powered-by-plateos-watermark">Powered by PlateOS</a>
          )}
        </div>
      </div>
    </footer>
  );
};
